import React, { useEffect, useState } from "react";
import CommonFooter from "../../components/footer/CommonFooter";
import CheckoutProduct from "./component/checkoutProduct/index.js";
import PaymentModal from "./component/paymentModal/index.js";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import FullScreenLoader from "../../components/loader/fullScreenLoader/FullscreenLoader";
import CommonButton from "../../components/button/CommonButton";
import {
  initializeRazorpayCheckout,
  getRazorpayConfig,
} from "../../utils/razorpay";
import "./Checkout.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { appApiHandler } from "../../api/appApiHandler";
import { getPrice } from "../../constants/globalFunction.js";
import { showErrorToast, showSuccessToast } from "../../utils/toast.js";
import { string } from "../../constants/string.js";
import HomeHeader from "../../components/header/HomeHeader";
import { RUPEE } from "../../constants/globalConstants";
import { setCartCount } from "../../store/slices/countState.js";
import Breadcrumb from "../../components/breadcrumb";
import EmptyState from "../../components/EmptyState/";
import { FaUser, FaShoppingCart } from "react-icons/fa";

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { address } = location.state;
  const { userData } = useSelector((state) => state.userState);
  const { isLoggedIn } = useSelector((state) => state.authState);

  const [cartItems, setCartItems] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [cod, setCod] = useState(true);
  const [online, setOnline] = useState(true);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [showConfirmation, setShowOrderConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const getCartProducts = async () => {
    const reqData = {
      user_id: userData?._id,
    };
    const response = await appApiHandler.getCart(reqData);
    setProductLoading(false);
    if (response?.status === 200 && response?.data) {
      setCartItems(response?.data?.products);
      dispatch(setCartCount(response?.data?.products?.length));
      if (response?.data?.tax_rates && address?.country_id) {
        const countryTaxRate = response?.data?.tax_rates?.find(
          (taxRate) => taxRate?.country_id === address?.country_id
        );
        if (countryTaxRate) {
          if (userData?.user_type === "wholeseller") {
            setTaxRate(countryTaxRate?.wholesale_tax_rate || 0);
          } else {
            setTaxRate(countryTaxRate?.retail_tax_rate || 0);
          }
        } else {
          setTaxRate(0);
        }
      }
      setCod(response?.data?.cod);
      setOnline(response?.data?.online);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      setProductLoading(true);
      getCartProducts();
    }
  }, [isLoggedIn]);

  const getProductPrice = async (size) => {
    const price = await getPrice(size);
    return price;
  };

  useEffect(() => {
    const calculateTotalAmount = async () => {
      const itemTotals = await Promise.all(
        cartItems?.map(async (item) => {
          const price = await getProductPrice(item?.size);
          return price * item?.qty;
        })
      );
      const total = itemTotals.reduce((acc, curr) => acc + curr, 0);
      setSubTotal(total);
    };
    calculateTotalAmount();
  }, [cartItems]);

  useEffect(() => {
    const taxableAmount = (subTotal * taxRate) / 100;
    setTotalAmount(subTotal + taxableAmount);
    setTaxAmount(taxableAmount);
  }, [taxRate, subTotal]);

  const handlePaymentModal = () => {
    if (userData?.user_type === "wholeseller") {
      setShowOrderConfirmation(true);
    } else {
      setPaymentModalVisible(true);
    }
  };

  const handlePayment = async (method) => {
    setPaymentModalVisible(false);
    if (method === 0) {
      const reqData = {
        user_id: userData?._id,
        address_id: address?._id,
        payment_method: "COD",
        paid: 0,
        total: totalAmount?.toFixed(2),
        tax_rate: taxRate,
      };
      setLoading(true);
      placeOrder(reqData);
    } else {
      setLoading(true);
      const orderData = {
        amount: totalAmount * 100,
        currency: "INR",
        user_id: userData?._id,
      };
      const response = await appApiHandler.createOrder(orderData);
      if (response?.status === 200 && response?.order_id) {
        proceedToCheckout(response?.order_id);
      } else if (response?.message) {
        setLoading(false);
        showErrorToast(response?.message);
      } else {
        setLoading(false);
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    }
  };

  const placeOrder = async (reqData) => {
    const response = await appApiHandler.placeOrder(reqData);
    console.log("response",response)
    setLoading(false);
    if (response?.status === 200) {
      showSuccessToast(response?.message);
      navigate("/");
    } else if (response?.message) {
      showErrorToast(response?.message);
    } else {
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const proceedToCheckout = async (orderId) => {
    setLoading(true);

    try {
      // Get Razorpay configuration
      const config = getRazorpayConfig();

      // Initialize Razorpay checkout
      const response = await initializeRazorpayCheckout({
        ...config,
        amount: totalAmount * 100,
        order_id: orderId,
        prefill: {
          contact: address?.contact,
          email: userData?.email,
          name: userData?.name,
        },
      })
        .then((data) => {
          if (data?.razorpay_payment_id) {
            const reqData = {
              user_id: userData?._id,
              address_id: address?._id,
              payment_method: "Online",
              paid: 1,
              total: totalAmount?.toFixed(2),
              tax_rate: taxRate,
              payment_id: data?.razorpay_payment_id,
            };
            setLoading(true);
            placeOrder(reqData);
          }
        })
        .catch((error) => {
          console.error("Razorpay payment gateway error:", error);
          setLoading(false);

          if (
            error?.message?.includes("cancelled") ||
            error?.message?.includes("canceled")
          ) {
            showErrorToast("Payment was cancelled");
          } else if (error?.message?.includes("failed")) {
            showErrorToast("Payment failed. Please try again.");
          } else if (
            error?.message?.includes("network") ||
            error?.message?.includes("connection")
          ) {
            showErrorToast(
              "Network error. Please check your connection and try again."
            );
          } else {
            showErrorToast("Payment failed. Please try again.");
          }
        });
    } catch (error) {
      console.error("Razorpay payment error:", error);
      setLoading(false);
      if (error?.code === 0) {
        showErrorToast("Payment cancelled");
      } else {
        showErrorToast(string.error.failed_due_to_internal_error);
      }
    }
  };

  const handleOrderConfirmation = () => {
    setShowOrderConfirmation(false);
    const reqData = {
      user_id: userData?._id,
      address_id: address?._id,
      total: totalAmount?.toFixed(2),
      tax_rate: taxRate,
    };
    setLoading(true);
    placeOrder(reqData);
  };

  const onConfirmationCancel = () => {
    setShowOrderConfirmation(false);
  };

  if (productLoading) {
    return (
      <div className="checkout-container">
      {/* <HomeHeader /> */}
        <Breadcrumb
          items={[
            {
              label: "Home",
              path: "/",
            },
            {
              label: "Checkout",
              isActive: true,
            },
          ]}
        />
        <div className="checkout-loading">
          <div className="loading-spinner"></div>
          <p>Loading cart items...</p>
        </div>
        <CommonFooter />
      </div>
    );
  }

  return (
    <div className="checkout-container">
      {/* <HomeHeader /> */}
      <div className="page-content">
        <Breadcrumb
          items={[
            {
              label: "Home",
              path: "/",
            },
            {
              label: "Checkout",
              isActive: true,
            },
          ]}
        />
        {!isLoggedIn ? (
          <EmptyState
            icon={FaUser}
            title="Sign In to Continue"
            description="please login with your account. Enjoy a seamless shopping experience with personalized recommendations and more!"
            buttonText="Login"
            onButtonClick={() => navigate("/login")}
            iconSize={48}
          />
        ) : (
          <div className="checkout-content">
            <div className="checkout-scroll">
              {cartItems.map((item, index) => (
                <CheckoutProduct key={index} item={item} />
              ))}

              <div className="address-container">
                <h3 className="address-title">Shipping Address</h3>
                <div className="address-card">
                  <p className="name-text">{address?.name}</p>
                  <p className="address-text">{address?.address}</p>
                  <p className="address-text">{address?.city}</p>
                  <p className="address-text">
                    {address?.state}, {address?.country?.name} -{" "}
                    {address?.pincode}
                  </p>
                  <p className="address-text">
                    Contact No: {address?.country_code}
                    {address?.contact}
                  </p>
                </div>
              </div>

              {taxAmount > 0 && (
                <div className="address-container">
                  <h3 className="address-title">Payment Summary</h3>
                  <div className="address-card">
                    <div className="summary-container">
                      <span className="title-text">Sub Total</span>
                      <span className="value-text">
                        {RUPEE}
                        {subTotal?.toFixed(2)}
                      </span>
                    </div>
                    <div className="summary-container">
                      <span className="title-text">Tax Rate ({taxRate}%)</span>
                      <span className="value-text">
                        {RUPEE}
                        {taxAmount?.toFixed(2)}
                      </span>
                    </div>
                    <div className="summary-container">
                      <span className="title-text">Total</span>
                      <span className="value-text">
                        {RUPEE}
                        {totalAmount?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="total-amount-container">
                <span className="total-amount">Payable Amount</span>
                <span className="total-amount-text">
                  {RUPEE}
                  {totalAmount?.toFixed(2)}
                </span>
              </div>

              <CommonButton
                title="Proceed to Checkout"
                onPress={handlePaymentModal}
                customClassName="checkout-button"
                icon={FaShoppingCart}
              />
            </div>
          </div>
        )}

        <PaymentModal
          visible={paymentModalVisible}
          onClose={() => setPaymentModalVisible(false)}
          onHandleSelect={handlePayment}
          cod={cod}
          online={online}
        />

        <ConfirmationModal
          visible={showConfirmation}
          title="Confirmation"
          description="Are you sure you want to place this order?"
          rightButtonText="Yes"
          leftButtonText="No"
          onRightButtonPress={handleOrderConfirmation}
          onLeftButtonPress={onConfirmationCancel}
        />

        {loading && <FullScreenLoader text="Placing order..." />}
      </div>
      <CommonFooter />
    </div>
  );
};

export default Checkout;
